/**
 * ad-diamond-finder.js
 * Atelier Diamante — Diamond Finder
 *
 * Data source: Shopify Storefront API (GraphQL)
 * - Reads window.adTheme.storefrontToken + window.adTheme.shop
 * - Collection handle from [data-collection] on the root element
 * - Fetches up to 250 products per cursor page, all pages until exhausted
 * - Filters client-side using product metafields (custom namespace)
 * - Falls back to mock data if no token is configured (dev/preview)
 */

// ── GraphQL query — dual-key aliases cover both new (diamond_*) and legacy CSV keys ──
const GQL_PRODUCTS_IN_COLLECTION = `
query DiamondFinderPage($handle: String!, $first: Int!, $cursor: String) {
  collection(handle: $handle) {
    products(first: $first, after: $cursor) {
      pageInfo { hasNextPage endCursor }
      edges {
        node {
          id
          title
          handle
          priceRange { minVariantPrice { amount currencyCode } }
          featuredImage { url(transform: { maxWidth: 500 }) altText }
          shapeNew:    metafield(namespace: "custom", key: "diamond_shape")    { value }
          shapeOld:    metafield(namespace: "custom", key: "shape")            { value }
          caratNew:    metafield(namespace: "custom", key: "diamond_carat")    { value }
          caratOld:    metafield(namespace: "custom", key: "carat_weight")     { value }
          colorNew:    metafield(namespace: "custom", key: "diamond_color")    { value }
          colorOld:    metafield(namespace: "custom", key: "color_grade")      { value }
          clarityNew:  metafield(namespace: "custom", key: "diamond_clarity")  { value }
          clarityOld:  metafield(namespace: "custom", key: "clarity_grade")    { value }
          cutNew:      metafield(namespace: "custom", key: "diamond_cut")      { value }
          cutOld:      metafield(namespace: "custom", key: "cut_grade")        { value }
          polishField: metafield(namespace: "custom", key: "diamond_polish")   { value }
          certNew:     metafield(namespace: "custom", key: "certificate_type") { value }
          certOld:     metafield(namespace: "custom", key: "lab")              { value }
          growthField: metafield(namespace: "custom", key: "growth_type")      { value }
          viewer360:   metafield(namespace: "custom", key: "viewer_360_url")  { value }
        }
      }
    }
  }
}`;

const GQL_PRODUCTS_ALL = `
query DiamondFinderAll($first: Int!, $cursor: String) {
  products(first: $first, after: $cursor) {
    pageInfo { hasNextPage endCursor }
    edges {
      node {
        id
        title
        handle
        priceRange { minVariantPrice { amount currencyCode } }
        featuredImage { url(transform: { maxWidth: 500 }) altText }
        shapeNew:    metafield(namespace: "custom", key: "diamond_shape")    { value }
        shapeOld:    metafield(namespace: "custom", key: "shape")            { value }
        caratNew:    metafield(namespace: "custom", key: "diamond_carat")    { value }
        caratOld:    metafield(namespace: "custom", key: "carat_weight")     { value }
        colorNew:    metafield(namespace: "custom", key: "diamond_color")    { value }
        colorOld:    metafield(namespace: "custom", key: "color_grade")      { value }
        clarityNew:  metafield(namespace: "custom", key: "diamond_clarity")  { value }
        clarityOld:  metafield(namespace: "custom", key: "clarity_grade")    { value }
        cutNew:      metafield(namespace: "custom", key: "diamond_cut")      { value }
        cutOld:      metafield(namespace: "custom", key: "cut_grade")        { value }
        polishField: metafield(namespace: "custom", key: "diamond_polish")   { value }
        certNew:     metafield(namespace: "custom", key: "certificate_type") { value }
        certOld:     metafield(namespace: "custom", key: "lab")              { value }
        growthField: metafield(namespace: "custom", key: "growth_type")      { value }
        viewer360:   metafield(namespace: "custom", key: "viewer_360_url")  { value }
      }
    }
  }
}`;

// ── Normalise a Storefront API product node → flat finder object ───────────────
function normaliseProduct(node) {
  const price  = parseFloat(node.priceRange?.minVariantPrice?.amount || 0);
  const rawCut = node.cutNew?.value || node.cutOld?.value || '';
  const cutLabels = { 'ID': 'Ideal', 'EX': 'Excellent', 'VG': 'Very Good', 'G': 'Good' };
  const caratRaw = node.caratNew?.value || node.caratOld?.value || '';

  return {
    id:         node.id,
    title:      node.title,
    handle:     node.handle,
    url:        '/products/' + node.handle,
    image:      node.featuredImage?.url || '',
    price,
    priceFormatted: '$' + Math.round(price).toLocaleString('en-US'),
    shape:      node.shapeNew?.value   || node.shapeOld?.value   || '',
    carat:      parseFloat(caratRaw) || 0,
    caratLabel: caratRaw,
    color:      node.colorNew?.value   || node.colorOld?.value   || '',
    clarity:    node.clarityNew?.value || node.clarityOld?.value || '',
    cut:        cutLabels[rawCut] || rawCut,
    cert:       node.certNew?.value    || node.certOld?.value    || '',
    growthType: node.growthField?.value || '',
    viewer_360_url: node.viewer360?.value || '',
  };
}

// ── Storefront API fetch (handles cursor pagination) ───────────────────────────
// onPage(productsSoFar, hasMore) fires after EVERY page so the UI can render
// progressively instead of waiting for the full catalog (~24 requests at 5.7k stones).
async function fetchAllDiamonds(shop, token, collectionHandle, onPage) {
  const endpoint = `https://${shop}/api/2024-10/graphql.json`;
  const headers = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': token,
  };

  // Try collection first, fall back to all products if collection doesn't exist
  let allProducts = [];

  if (collectionHandle) {
    allProducts = await _fetchPaginated(endpoint, headers, GQL_PRODUCTS_IN_COLLECTION, { handle: collectionHandle }, true, onPage);
    if (allProducts.length > 0) return allProducts;
    console.info('Diamond Finder: collection "' + collectionHandle + '" empty or missing — loading all products');
  }

  // Fallback: fetch all products (no collection filter)
  return _fetchPaginated(endpoint, headers, GQL_PRODUCTS_ALL, {}, false, onPage);
}

async function _fetchPaginated(endpoint, headers, query, extraVars, useCollection, onPage) {
  let allProducts = [];
  let cursor = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const variables = { first: 250, cursor, ...extraVars };

    const res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables }),
    });

    if (!res.ok) throw new Error(`Storefront API ${res.status}`);

    const json = await res.json();
    if (json.errors) throw new Error(json.errors[0]?.message || 'GraphQL error');

    const conn = useCollection
      ? json.data?.collection?.products
      : json.data?.products;

    if (!conn) break;

    allProducts.push(...conn.edges.map(e => normaliseProduct(e.node)));
    hasNextPage = conn.pageInfo.hasNextPage;
    cursor = conn.pageInfo.endCursor;

    // Progressive render hook — first page paints the grid immediately
    if (onPage && allProducts.length) onPage(allProducts, hasNextPage);
  }

  return allProducts;
}

// ── Alpine.js component ────────────────────────────────────────────────────────
function adDiamondFinder() {
  return {
    filters: {
      shapes:    [],
      caratMin:  0.3,
      caratMax:  30.0,
      colors:    [],
      clarities: [],
      cuts:      [],
      priceMin:  0,
      priceMax:  500000,
      origin:    '',
      certifications: [],
      colorType: '',   /* '' = any, 'colorless' = D-Z grades, 'fancy' = FANCY ... */
    },

    allProducts:    [],  // full fetched set
    products:       [],  // filtered + paginated view
    compareList:    [],
    loading:        true,
    noToken:        false,
    showCompareDrawer: false,
    showSpecialty:  false,  // specialty cuts panel
    showMore:       false,  // advanced filters panel (Color/Clarity/Cut/Price/Origin/Cert)
    sortBy:         'featured',
    resultCount:    0,
    page:           1,
    perPage:        24,
    debounceTimer:  null,

    init() {
      this.loadFromURL();

      const tok  = window.adTheme?.storefrontToken || '';
      const shop = window.adTheme?.shop || window.Shopify?.shop || '';
      const col  = window.adTheme?.collection || '';

      // 1. Storefront API (full catalog) — preferred when token is present
      if (tok) {
        // 1a. Session cache — instant paint on repeat visits (30 min TTL),
        //     then refresh silently in the background.
        const cached = this._readCache();
        if (cached) {
          this.allProducts = cached;
          this._filterProducts();
          this.loading = false;
          console.info('Diamond Finder: ' + cached.length + ' products from session cache');
        }

        // 1b. Progressive fetch — grid renders after the FIRST page (~250 stones),
        //     remaining pages stream in and the counts tick up.
        fetchAllDiamonds(shop, tok, col, (productsSoFar) => {
          this.allProducts = productsSoFar;
          this._filterProducts();   // direct call: no debounce, no page reset
          this.loading = false;
        })
          .then(products => {
            if (products.length > 0) {
              this.allProducts = products;
              this._filterProducts();
              this._writeCache(products);
              console.info('Diamond Finder: loaded ' + products.length + ' products via Storefront API');
            } else if (!cached) {
              console.warn('Diamond Finder: Storefront API returned 0 products — trying Liquid data');
              this._loadLiquidData();
            }
          })
          .catch(err => {
            console.error('Diamond Finder: Storefront API error —', err.message);
            // Fall through to Liquid data only if nothing rendered yet
            if (!cached && this.allProducts.length === 0) this._loadLiquidData();
          })
          .finally(() => { this.loading = false; });

        window.addEventListener('popstate', () => { this.loadFromURL(); this.applyFilters(); });
        return;
      }

      // 2. Server-rendered Liquid JSON — used when no Storefront token
      if (this._loadLiquidData()) return;

      // 3. No token and no Liquid data — show mock
      this.noToken = true;
      this.allProducts = this.mockProducts();
      this.applyFilters();
      this.loading = false;

      window.addEventListener('popstate', () => { this.loadFromURL(); this.applyFilters(); });
    },

    _loadLiquidData() {
      const dataEl = document.getElementById('ad-finder-data');
      if (!dataEl) return false;
      try {
        const raw = JSON.parse(dataEl.textContent);
        this.allProducts = raw.map(p => ({
          ...p,
          carat:      parseFloat(p.carat) || 0,
          caratLabel: String(p.carat || ''),
          priceFormatted: '$' + Math.round(p.price).toLocaleString('en-US'),
        }));
      } catch (e) {
        console.error('Diamond Finder: could not parse embedded data —', e);
        this.allProducts = [];
      }
      this.applyFilters();
      this.loading = false;
      window.addEventListener('popstate', () => { this.loadFromURL(); this.applyFilters(); });
      return true;
    },

    _storefrontError(err) {
      this.allProducts = [];
      this.products = [];
    },

    // ── Session cache (sessionStorage, 30 min TTL) ─────────────────────────────
    _readCache() {
      try {
        const raw = sessionStorage.getItem('adFinderCache_v1');
        if (!raw) return null;
        const { ts, products } = JSON.parse(raw);
        if (Date.now() - ts > 30 * 60 * 1000) return null;
        if (!Array.isArray(products) || products.length === 0) return null;
        return products;
      } catch (e) { return null; }
    },

    _writeCache(products) {
      try {
        sessionStorage.setItem('adFinderCache_v1', JSON.stringify({ ts: Date.now(), products }));
      } catch (e) { /* quota exceeded — skip caching, no harm */ }
    },

    // ── Filtering ──────────────────────────────────────────────────────────────
    applyFilters() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.page = 1;
        this.pushURL();
        this._filterProducts();
      }, 250);
    },

    _filterProducts() {
      const f = this.filters;
      let result = this.allProducts.filter(p => {
        if (f.shapes.length    && !f.shapes.includes(p.shape))       return false;
        if (f.colors.length    && !f.colors.includes(p.color))       return false;
        if (f.clarities.length && !f.clarities.includes(p.clarity))  return false;
        if (f.cuts.length      && !f.cuts.includes(p.cut))           return false;
        if (f.certifications.length && !f.certifications.includes(p.cert)) return false;
        if (p.carat < f.caratMin || p.carat > f.caratMax)            return false;
        // Storefront API returns dollars; compare directly
        if (p.price < f.priceMin || p.price > f.priceMax)            return false;
        if (f.origin && p.growthType &&
            !p.growthType.toLowerCase().includes(f.origin.toLowerCase())) return false;
        /* Color-type segmentation: white/colorless vs. fancy colored diamonds */
        if (f.colorType === 'fancy'     && !/^FANCY/i.test(p.color || '')) return false;
        if (f.colorType === 'colorless' &&  /^FANCY/i.test(p.color || '')) return false;
        return true;
      });

      // -- Sort --
      switch (this.sortBy) {
        case 'price-asc':  result.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
        case 'price-desc': result.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
        case 'carat-asc':  result.sort((a, b) => (a.carat || 0) - (b.carat || 0)); break;
        case 'carat-desc': result.sort((a, b) => (b.carat || 0) - (a.carat || 0)); break;
        default: break; // 'featured' keeps source order
      }

      this.resultCount = result.length;
      this.products = result.slice(0, this.page * this.perPage);
      this._totalFiltered = result.length;
    },

    get hasMore() {
      return this.products.length < (this._totalFiltered || 0);
    },

    loadMore() {
      this.page++;
      this._filterProducts();
    },

    toggleShape(shape) {
      const idx = this.filters.shapes.indexOf(shape);
      idx > -1 ? this.filters.shapes.splice(idx, 1) : this.filters.shapes.push(shape);
      this.applyFilters();
    },

    toggleFilter(key, value) {
      const arr = this.filters[key];
      const idx = arr.indexOf(value);
      idx > -1 ? arr.splice(idx, 1) : arr.push(value);
      this.applyFilters();
    },

    updateCaratRange(e) {
      const inputs = e.target.parentElement.querySelectorAll('.ad-range-input');
      let min = parseFloat(inputs[0].value);
      let max = parseFloat(inputs[1].value);
      if (min > max) [min, max] = [max, min];
      this.filters.caratMin = min;
      this.filters.caratMax = max;
      this.updateRangeVisuals(e.target.parentElement);
      this.applyFilters();
    },

    updatePriceRange(e) {
      const inputs = e.target.parentElement.querySelectorAll('.ad-range-input');
      let min = parseFloat(inputs[0].value);
      let max = parseFloat(inputs[1].value);
      if (min > max) [min, max] = [max, min];
      this.filters.priceMin = min;
      this.filters.priceMax = max;
      this.updateRangeVisuals(e.target.parentElement);
      this.applyFilters();
    },

    updateRangeVisuals(rangeEl) {
      const inputs = rangeEl.querySelectorAll('.ad-range-input');
      const fill   = rangeEl.querySelector('.ad-range-fill');
      const min    = parseFloat(inputs[0].min);
      const max    = parseFloat(inputs[0].max);
      const minVal = parseFloat(inputs[0].value);
      const maxVal = parseFloat(inputs[1].value);

      const minPct = ((minVal - min) / (max - min)) * 100;
      const maxPct = ((maxVal - min) / (max - min)) * 100;

      if (fill) {
        fill.style.left  = minPct + '%';
        fill.style.width = (maxPct - minPct) + '%';
      }

      const minLabel = rangeEl.querySelector('.ad-price-min');
      const maxLabel = rangeEl.querySelector('.ad-price-max');
      if (minLabel) minLabel.textContent = Math.round(minVal).toLocaleString();
      if (maxLabel) maxLabel.textContent = Math.round(maxVal).toLocaleString();
    },

    clearFilters() {
      this.filters = {
        shapes: [], caratMin: 0.5, caratMax: 5.0,
        colors: [], clarities: [], cuts: [],
        priceMin: 1000, priceMax: 250000, origin: '', certifications: [],
      };
      this.page = 1;
      this.compareList = [];
      this.pushURL();
      this._filterProducts();
    },

    get activeFilterCount() {
      const f = this.filters;
      let n = f.shapes.length + f.colors.length + f.clarities.length +
              f.cuts.length + f.certifications.length;
      if (f.caratMin !== 0.5 || f.caratMax !== 5.0) n++;
      if (f.priceMin !== 1000 || f.priceMax !== 250000) n++;
      if (f.origin) n++;
      if (f.colorType) n++;
      return n;
    },

    // ── URL sync ───────────────────────────────────────────────────────────────
    loadFromURL() {
      const sp = new URL(window.location).searchParams;
      this.filters.shapes         = sp.getAll('shape');
      this.filters.caratMin       = parseFloat(sp.get('carat_min')) || 0.5;
      this.filters.caratMax       = parseFloat(sp.get('carat_max')) || 5.0;
      this.filters.colors         = sp.getAll('color');
      this.filters.clarities      = sp.getAll('clarity');
      this.filters.cuts           = sp.getAll('cut');
      this.filters.priceMin       = parseFloat(sp.get('price_min')) || 1000;
      this.filters.priceMax       = parseFloat(sp.get('price_max')) || 250000;
      this.filters.origin         = sp.get('origin') || '';
      this.filters.certifications = sp.getAll('cert');
      this.filters.colorType      = sp.get('color_type') || '';
    },

    pushURL() {
      const f = this.filters;
      const sp = new URLSearchParams();
      f.shapes.forEach(v => sp.append('shape', v));
      f.colors.forEach(v => sp.append('color', v));
      f.clarities.forEach(v => sp.append('clarity', v));
      f.cuts.forEach(v => sp.append('cut', v));
      f.certifications.forEach(v => sp.append('cert', v));
      if (f.caratMin !== 0.5)   sp.set('carat_min', f.caratMin);
      if (f.caratMax !== 5.0)   sp.set('carat_max', f.caratMax);
      if (f.priceMin !== 1000)  sp.set('price_min', f.priceMin);
      if (f.priceMax !== 250000) sp.set('price_max', f.priceMax);
      if (f.origin)             sp.set('origin', f.origin);
      if (f.colorType)          sp.set('color_type', f.colorType);

      const qs = sp.toString();
      const url = window.location.pathname + (qs ? '?' + qs : '');
      window.history.replaceState(null, '', url);
    },

    // ── Compare ───────────────────────────────────────────────────────────────
    toggleCompare(product) {
      const idx = this.compareList.findIndex(p => p.id === product.id);
      if (idx > -1) {
        this.compareList.splice(idx, 1);
      } else if (this.compareList.length < 4) {
        this.compareList.push(product);
      }
    },

    removeFromCompare(product) {
      this.compareList = this.compareList.filter(p => p.id !== product.id);
    },

    // ── Helpers ───────────────────────────────────────────────────────────────
    formatPrice(price) {
      if (!price && price !== 0) return '—';
      return '$' + Math.round(price).toLocaleString('en-US');
    },

    get totalCount() {
      return this._totalFiltered || 0;
    },

    // ── Shape SVG paths ──────────────────────────────────────────────────────
    shapeViewBox(shape) {
      return '0 0 40 40';
    },

    shapeSvg(shape) {
      const shapes = {
        // ── Standard cuts ────────────────────────────────────────────────────
        'Round':    '<circle cx="20" cy="20" r="14" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Oval':     '<ellipse cx="20" cy="20" rx="10" ry="15" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Emerald':  '<rect x="8" y="6" width="24" height="28" rx="3" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Cushion':  '<rect x="7" y="7" width="26" height="26" rx="6" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Pear':     '<path d="M20 5 C12 14, 8 22, 8 28 C8 34, 13 37, 20 37 C27 37, 32 34, 32 28 C32 22, 28 14, 20 5Z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Marquise': '<path d="M20 4 C14 12, 6 18, 6 20 C6 22, 14 28, 20 36 C26 28, 34 22, 34 20 C34 18, 26 12, 20 4Z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Radiant':  '<polygon points="20,4 34,12 34,28 20,36 6,28 6,12" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Princess': '<rect x="8" y="8" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Asscher':  '<rect x="9" y="9" width="22" height="22" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        'Heart':    '<path d="M20 34 C14 28, 4 22, 4 14 C4 9, 8 5, 12 5 C15 5, 18 7, 20 10 C22 7, 25 5, 28 5 C32 5, 36 9, 36 14 C36 22, 26 28, 20 34Z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        // ── Specialty cuts ───────────────────────────────────────────────────
        // Pie Cut: teardrop/pear silhouette with large step-facet geometry
        'Pie Cut':  '<path d="M20 5 C12 14, 8 22, 8 29 C8 35, 13 37, 20 37 C27 37, 32 35, 32 29 C32 22, 28 14, 20 5Z" fill="none" stroke="currentColor" stroke-width="1.5"/><line x1="20" y1="5" x2="8" y2="29" stroke="currentColor" stroke-width="0.7" opacity="0.5"/><line x1="20" y1="5" x2="32" y2="29" stroke="currentColor" stroke-width="0.7" opacity="0.5"/><line x1="10" y1="24" x2="30" y2="24" stroke="currentColor" stroke-width="0.7" opacity="0.5"/>',
        // Triangular Brilliant (Trillion): equilateral triangle, curved sides
        'Triangular Brilliant': '<path d="M20 5 L35 32 L5 32 Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><line x1="20" y1="5" x2="20" y2="32" stroke="currentColor" stroke-width="0.7" opacity="0.5"/><line x1="12" y1="18" x2="28" y2="18" stroke="currentColor" stroke-width="0.7" opacity="0.5"/>',
        // Oval Polygon: elongated octagon (oval with clipped corners)
        'Oval Polygon': '<polygon points="14,6 26,6 34,14 34,26 26,34 14,34 6,26 6,14" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        // Pentagonal: regular pentagon
        'Pentagonal': '<polygon points="20,5 35,16 30,33 10,33 5,16" fill="none" stroke="currentColor" stroke-width="1.5"/>',
        // Hexagonal: regular hexagon
        'Hexagonal':  '<polygon points="20,5 33,12 33,28 20,35 7,28 7,12" fill="none" stroke="currentColor" stroke-width="1.5"/>',
      };
      return shapes[shape] || shapes['Round'];
    },

    // ── Mock data (dev/preview mode) ─────────────────────────────────────────
    mockProducts() {
      const shapes = ['Round', 'Oval', 'Emerald', 'Cushion', 'Pear', 'Marquise', 'Radiant', 'Princess'];
      const colors = ['D', 'E', 'F', 'G', 'H', 'I'];
      const clarities = ['VVS1', 'VVS2', 'VS1', 'VS2', 'SI1'];
      const cuts = ['Ideal', 'Excellent', 'Very Good'];
      const certs = ['IGI', 'GIA'];

      return Array.from({ length: 48 }, (_, i) => ({
        id:        'mock-' + (i + 1),
        title:     `Lab-Grown Diamond ${(1 + Math.random() * 3).toFixed(2)}ct ${shapes[i % shapes.length]}`,
        handle:    'mock-diamond-' + (i + 1),
        url:       '#',
        image:     '',
        price:     1500 + Math.floor(Math.random() * 15000),
        carat:     +(1 + Math.random() * 3).toFixed(2),
        caratLabel: (1 + Math.random() * 3).toFixed(2),
        shape:     shapes[i % shapes.length],
        color:     colors[i % colors.length],
        clarity:   clarities[i % clarities.length],
        cut:       cuts[i % cuts.length],
        cert:      certs[i % certs.length],
        growthType: 'Lab-Grown',
        priceFormatted: '',
      }));
    },
  };
}
